import { Component, Injector, Input, OnInit } from '@angular/core';
import { ComponentBase, ComponentColor } from '../component-base';

export type ProgressSegment = {
  label: string,
  value: number,
  start?: number,
  hint?: string,
  striped?: boolean,
  opacity?: number,
  color: ComponentColor
};

/**
 * O progress-bar pode ser multiplo (setando os segments), ou simples (setando value);
 * Quando múltiplo, podereá utilizar valores absolutos (stacked), ou relativos. Caso stacked,
 * será utilizado o start do segment para determinar o início, e se for omitido irá iniciar de 0.
 * A propriedade min subtrai o valor de todos, servirá como referência inicial (valor onde o zero do progress irá iniciar);
 * O max só será utilizado caso não tenha segmentos, e serve para calcular o range dos valores. 
 **/

@Component({
  selector: 'progress-bar',
  templateUrl: './progress-bar.component.html',
  styleUrls: ['./progress-bar.component.scss']
})
export class ProgressBarComponent extends ComponentBase {
  @Input() max: number = 100;
  @Input() min: number = 0;
  @Input() value: number = 0;
  @Input() height: number = 12;
  @Input() noPercentage?: string;
  @Input() minWidth?: number;
  @Input() title?: string;
  @Input() color?: ComponentColor;
  @Input() set stacked(value: string | undefined) {
    if(this._stacked != value) {
      this._stacked = value;
      this.updateSegments();
    }
  };
  get stacked(): string | undefined {
    return this._stacked;
  }
  @Input() set segments(value: ProgressSegment[] | undefined) {
    if(this._segments != value) {
      this._segments = value;
      this.updateSegments();
    }
  };
  get segments(): ProgressSegment[] | undefined {
    return this._segments;
  }
  
  public orderedSegments?: ProgressSegment[];

  private _segments?: ProgressSegment[];
  private _stacked?: string;
  private _overIndex?: number;
  private static colors = ["primary", "secondary", "success", "danger", "warning", "info", "light", "dark"];

  constructor(public injector: Injector) {
    super(injector);
  }

  public get hasSegments(): boolean {
    return !!this.segments?.length;
  }

  public get isStacked(): boolean {
    return this.stacked != undefined;
  }

  public absolute(segment: ProgressSegment) {
    return segment.value + (segment.start || 0);
  }

  public updateSegments() {
    if(this.isStacked) {
      this.orderedSegments = this.segments?.sort((a, b) => {
        const absA = this.absolute(a);
        const absB = this.absolute(b);
        return absA == absB ? b.value - a.value : absB - absA;
      });
    } else {
      this.orderedSegments = this.segments;
    }
    this.max = this.stackedMax;//this.segments?.reduce((a, v) => a = Math.max(a, this.absolute(v)), 0) || 100;
    this.detectChanges();
  }

  public hintHasTags(segment: ProgressSegment) {
    return !!segment.hint?.match(/<.+>/);
  }

  public get stackedMax(): number {
    return this.segments?.reduce((a, v) => a = this.isStacked ? Math.max(this.absolute(v), a) : a + v.value, 0) || 100;
  }

  public start(segment: ProgressSegment): number {
    const range = (segment ? this.stackedMax : (this.max - this.min));
    return Math.round((segment.start || 0) * 100 / range);
  }

  public percentage(segment?: ProgressSegment): number {
    //const value = Math.max((segment ? segment.value - (segment.start || 0) : this.value) - this.min, 0);
    const value = Math.max((segment ? segment.value : this.value) - this.min, 0);
    const range = (segment ? this.stackedMax : (this.max - this.min));
    return Math.round(value * 100 / range);
  }

  public get isNoPercentage(): boolean {
    return this.noPercentage != undefined;
  }

  public progressClass(segment?: ProgressSegment): string {
    const color = segment?.color || this.color || ProgressBarComponent.colors.filter(x => !this.segments!.find(y => y.color == x))[0] || "success";
    return "progress-bar" + (!segment || typeof segment.striped == "undefined" || segment.striped ? " progress-bar-striped" : "") + " bg-" + color;
  }

  public progressStyle(segment?: ProgressSegment): string | undefined {
    return !segment || typeof segment.opacity == "undefined" ? undefined : "--bs-bg-opacity: " + (segment.opacity / 100);
  }

  public opacity(segment: ProgressSegment) {
    return typeof segment.opacity == "undefined" ? undefined : segment.opacity / 100;
  }

  public zIndex(index: number) {
    return this._overIndex == index ? this.orderedSegments!.length + 1 : index;
  }

  public mouseEnter(segment: ProgressSegment, index: number) {
    this._overIndex = index;
    this.detectChanges();
  }

  public mouseLeave(segment: ProgressSegment, index: number) {
    this._overIndex = undefined;
    this.detectChanges();
  }
}
